import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from './useAuth'
import { mapContract } from '@/mappers/contract.mapper'
import { productKeyForNormalizedName } from '@/data/bundleReference'
import { CROSS_PRODUCT_COVERAGE } from '@/config/productCoverage'
import type { ProductKey } from '@/router/products'
import type { MemberContract } from '@/data/seedMember'

/** Contract statuses that no longer count as "subscribed" (a terminated/finished
 *  contract must not block re-subscribing to the same product). */
const INACTIVE_STATUSES = new Set(['terminated', 'completed'])

/**
 * Connected-space contracts as `MemberContract` view-models.
 *
 * The raw contracts are fetched once (in `useAuth`); this composable maps them
 * with the current locale + translator, so the mapping (localized payment
 * labels, guarantees in the right language) updates reactively when the locale
 * changes. Must be called from component setup (needs i18n).
 */
export function useContracts() {
  const { t, locale } = useI18n()
  const { rawContracts, contractsLoading, reloadContracts } = useAuth()

  const contracts = computed(() =>
    rawContracts.value.map((raw) => mapContract(raw, t, locale.value)),
  )

  // The member's current (non-terminated) contract view-model per journey. Drives
  // both the "already subscribed" state and the per-product actions (e.g. the
  // terminate button) of the subscribe page. The contract → ProductKey rule
  // lives in bundleReference (single source).
  const contractByProduct = computed(() => {
    const map = new Map<ProductKey, MemberContract>()
    for (const raw of rawContracts.value) {
      if (INACTIVE_STATUSES.has(raw.status)) continue
      const key = productKeyForNormalizedName(raw.bundle_normalized_name)
      if (key && !map.has(key)) map.set(key, mapContract(raw, t, locale.value))
    }
    return map
  })
  // Premier contrat actif remonté à FSME (`fsme_eligible`), ou null. Un tel
  // contrat conditionne à la fois la carte de tiers payant et le RIB de
  // remboursement (affichage, nav, accès page), et sert de cible à la création
  // du premier RIB (le `banking_account_detail` s'y rattache).
  const fsmeContract = computed(
    () => rawContracts.value.find((raw) => !INACTIVE_STATUSES.has(raw.status) && raw.fsme_eligible) ?? null,
  )
  // Vrai dès qu'un contrat actif est remonté à FSME. Pilote l'affichage de la
  // carte (tuile, encart, nav) et l'accès aux pages dédiées.
  const hasFsmeCard = computed(() => fsmeContract.value !== null)

  /** The `bundle_normalized_name`s of every active contract held by the member. */
  const heldBundleNames = computed(
    () =>
      new Set(
        rawContracts.value
          .filter((c) => !INACTIVE_STATUSES.has(c.status))
          .map((c) => c.bundle_normalized_name)
          .filter((n): n is string => !!n),
      ),
  )

  // Products flagged "already subscribed": those the member holds a contract for,
  // plus the configurable cross-product coverage (e.g. solidariteExam is covered
  // by a compl_sante "pack") — see config/productCoverage.
  const subscribedProductKeys = computed(() => {
    const keys = new Set<ProductKey>(contractByProduct.value.keys())
    for (const key of Object.keys(CROSS_PRODUCT_COVERAGE) as ProductKey[]) {
      const coveringBundles = CROSS_PRODUCT_COVERAGE[key]
      if (coveringBundles?.some((name) => heldBundleNames.value.has(name))) {
        keys.add(key)
      }
    }
    return keys
  })

  // How many active contracts the member holds per journey. `contractByProduct` keeps only
  // the first of them, which is enough to name a product as subscribed but not to act on it:
  // an action offered on "the" contract has no meaning once there are several. Counted on the
  // same rule as above — a contract is active until it is terminated or completed, whatever
  // its dates, so a cover starting next month counts exactly like one running today.
  const contractCountByProduct = computed(() => {
    const counts = new Map<ProductKey, number>()
    for (const raw of rawContracts.value) {
      if (INACTIVE_STATUSES.has(raw.status)) continue
      const key = productKeyForNormalizedName(raw.bundle_normalized_name)
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return counts
  })

  /** How many active contracts the member holds for a product. */
  function contractCountForProduct(key: ProductKey): number {
    return contractCountByProduct.value.get(key) ?? 0
  }

  /** The active contract for a product, or null when none is held. */
  function contractForProduct(key: ProductKey): MemberContract | null {
    return contractByProduct.value.get(key) ?? null
  }

  /**
   * The contract a product's certificate should be downloaded from: the
   * product's own active contract when directly held, otherwise — for a
   * cross-sell product (covered via CROSS_PRODUCT_COVERAGE, e.g. solidariteExam
   * covered by a compl_sante pack) — the main contract that covers it. Returns
   * null when the product is neither held nor cross-covered.
   */
  function certificateContractForProduct(key: ProductKey): MemberContract | null {
    const direct = contractForProduct(key)
    if (direct) return direct
    const coveringBundles = CROSS_PRODUCT_COVERAGE[key]
    if (!coveringBundles) return null
    const raw = rawContracts.value.find(
      (c) =>
        !INACTIVE_STATUSES.has(c.status) &&
        !!c.bundle_normalized_name &&
        coveringBundles.includes(c.bundle_normalized_name),
    )
    return raw ? mapContract(raw, t, locale.value) : null
  }

  return {
    contracts,
    hasFsmeCard,
    fsmeContract,
    subscribedProductKeys,
    contractForProduct,
    contractCountForProduct,
    certificateContractForProduct,
    loading: contractsLoading,
    reload: reloadContracts,
  }
}
